import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '@/store';
import { api } from '@/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Plus, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_OPTIONS = [
  { value: '', label: '— Ignorar —' },
  { value: 'name', label: 'Nome *' },
  { value: 'phone', label: 'Telefone *' },
  { value: 'plan', label: 'Pacote' },
  { value: 'price', label: 'Valor/Mensalidade' },
  { value: 'expiration', label: 'Vencimento' },
  { value: 'notes', label: 'Observações' },
];

export default function BackupPage() {
  const { user } = useAuthStore();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const [missingPlans, setMissingPlans] = useState([]);
  const [planModal, setPlanModal] = useState(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planPrice, setPlanPrice] = useState('');
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Manual mapping state
  const [rawData, setRawData] = useState(null); // { headers, preview_rows, raw_rows }
  const [colMapping, setColMapping] = useState({});
  const [mappingStep, setMappingStep] = useState(false);
  const [importMode, setImportMode] = useState(null); // 'manual' | 'ai'

  const duplicateCount = parsed?.customers?.filter(c => c.duplicate).length || 0;

  // Cleanup on unmount
  useEffect(() => { return () => { resetImport(); }; }, []);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv') && !f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Use arquivo CSV ou XLSX'); return;
    }
    setFile(f);
    setParsed(null); setImportResult(null); setMissingPlans([]);
    setRawData(null); setMappingStep(false); setImportMode(null);
  };

  const handleUpload = async (mode) => {
    if (!file) return;
    setImportMode(mode);
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post(`/backup/parse?mode=${mode}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (mode === 'manual') {
        setRawData(r.data);
        // Auto-guess mapping from headers
        const guess = {};
        (r.data.headers || []).forEach((h, i) => {
          const hl = h.toLowerCase().trim();
          if (!guess.name && ['nome', 'name', 'cliente'].some(k => hl.includes(k))) guess.name = i;
          else if (!guess.phone && ['whatsapp', 'telefone', 'phone', 'celular', 'fone'].some(k => hl.includes(k))) guess.phone = i;
          else if (!guess.plan && ['plano', 'pacote', 'plan', 'produto'].some(k => hl.includes(k))) guess.plan = i;
          else if (!guess.price && ['valor', 'preco', 'price', 'mensalidade'].some(k => hl.includes(k))) guess.price = i;
          else if (!guess.expiration && ['vencimento', 'validade', 'expir', 'data'].some(k => hl.includes(k))) guess.expiration = i;
          else if (!guess.notes && ['obs', 'nota', 'note', 'observ'].some(k => hl.includes(k))) guess.notes = i;
        });
        setColMapping(guess);
        setMappingStep(true);
        toast.success(`${r.data.total_data_rows} linha(s) encontrada(s). Mapeie as colunas.`);
      } else {
        // AI mode - already processed
        setParsed(r.data);
        setMissingPlans(r.data.missing_plans || []);
        setIncludeDuplicates(false);
        const dupes = (r.data.customers || []).filter(c => c.duplicate).length;
        if (dupes > 0 || r.data.missing_plans?.length > 0) toast.warning(`${r.data.total_rows} clientes. ${dupes} duplicados, ${r.data.missing_plans?.length || 0} pacotes novos.`);
        else toast.success(`${r.data.total_rows} cliente(s) prontos!`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao analisar arquivo');
    } finally { setParsing(false); }
  };

  const handleApplyMapping = async () => {
    if (colMapping.name === undefined || colMapping.phone === undefined) {
      toast.error('Nome e Telefone são obrigatórios'); return;
    }
    setParsing(true);
    try {
      const mapping = {
        name_col: colMapping.name ?? -1,
        phone_col: colMapping.phone ?? -1,
        plan_col: colMapping.plan ?? -1,
        price_col: colMapping.price ?? -1,
        exp_col: colMapping.expiration ?? -1,
        notes_col: colMapping.notes ?? -1,
      };
      const r = await api.post('/backup/parse-mapped', {
        raw_rows: rawData.raw_rows, mapping, has_header: true
      });
      setParsed(r.data);
      setMissingPlans(r.data.missing_plans || []);
      setIncludeDuplicates(false);
      setMappingStep(false);
      const dupes = (r.data.customers || []).filter(c => c.duplicate).length;
      toast.success(`${r.data.total_rows} cliente(s) mapeado(s)${dupes > 0 ? ` (${dupes} duplicados)` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao processar');
    } finally { setParsing(false); }
  };

  const handleCreatePlan = async () => {
    if (!planModal) return;
    setCreatingPlan(true);
    try {
      const r = await api.post('/backup/create-plan', { name: planModal, price: parseFloat(planPrice) || 0 });
      toast.success(`Pacote "${planModal}" criado!`);
      const updated = { ...parsed };
      updated.customers = updated.customers.map(c =>
        c.plan_name.toLowerCase().trim() === planModal.toLowerCase().trim() ? { ...c, plan_id: r.data.id } : c
      );
      updated.existing_plans = [...updated.existing_plans, r.data];
      setParsed(updated);
      setMissingPlans(prev => prev.filter(p => p !== planModal));
      setPlanModal(null); setPlanPrice('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setCreatingPlan(false); }
  };

  const handleImport = async () => {
    if (!parsed?.customers?.length) return;
    setImporting(true); setImportProgress(0); setImportResult(null);
    const customers = parsed.customers.map(c => ({ ...c, force: c.duplicate && includeDuplicates }));
    const batchSize = 20;
    let totalImported = 0, totalSkipped = 0, totalErrors = [];
    for (let i = 0; i < customers.length; i += batchSize) {
      const batch = customers.slice(i, i + batchSize);
      try {
        const r = await api.post('/backup/import', { customers: batch });
        totalImported += r.data.imported; totalSkipped += r.data.skipped;
        if (r.data.errors?.length) totalErrors.push(...r.data.errors);
      } catch { totalErrors.push(`Erro no lote ${Math.floor(i / batchSize) + 1}`); }
      setImportProgress(Math.min(100, Math.round(((i + batchSize) / customers.length) * 100)));
    }
    setImportProgress(100);
    setImportResult({ imported: totalImported, skipped: totalSkipped, errors: totalErrors });
    setImporting(false);
    if (totalImported > 0) toast.success(`${totalImported} importado(s)!`);
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const r = await api.get(`/backup/export?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `clientes_backup.${format}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exportado!');
    } catch { toast.error('Erro ao exportar'); }
    finally { setExporting(false); }
  };

  const resetImport = () => {
    setFile(null); setParsed(null); setImportResult(null); setMissingPlans([]);
    setImportProgress(0); setRawData(null); setMappingStep(false); setImportMode(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const setMappingField = (field, colIdx) => {
    const newMap = { ...colMapping };
    // Remove field from previous column
    Object.keys(newMap).forEach(k => { if (newMap[k] === colIdx && k !== field) delete newMap[k]; });
    if (colIdx === '' || colIdx === undefined) { delete newMap[field]; }
    else { newMap[field] = parseInt(colIdx); }
    setColMapping(newMap);
  };

  const getFieldForCol = (colIdx) => {
    return Object.entries(colMapping).find(([_, v]) => v === colIdx)?.[0] || '';
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Backup</h1>
        <p className="text-sm text-muted-foreground">Importe e exporte seus clientes</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* IMPORT */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><Upload className="w-4 h-4 text-blue-500" />Importar Clientes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Envie um arquivo <strong>CSV</strong> ou <strong>Excel (.xlsx)</strong> com os dados dos seus clientes.
            </p>
            <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 space-y-2">
              <p className="text-xs font-semibold text-blue-500">Colunas aceitas:</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span><strong className="text-foreground">Nome</strong> — obrigatório</span>
                <span><strong className="text-foreground">Telefone</strong> — obrigatório</span>
                <span><strong className="text-foreground">Pacote</strong> — nome do plano</span>
                <span><strong className="text-foreground">Valor</strong> — mensalidade (R$)</span>
                <span><strong className="text-foreground">Vencimento</strong> — DD/MM/AAAA</span>
                <span><strong className="text-foreground">Observações</strong> — notas</span>
              </div>
              <p className="text-[10px] text-muted-foreground/70">Apenas Nome e Telefone são obrigatórios. Sem vencimento = 30 dias a partir de hoje.</p>
            </div>

            {/* Step 1: File select */}
            {!parsed && !mappingStep && !importResult && (
              <>
                <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 transition-colors group">
                  <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-muted-foreground group-hover:text-primary transition-colors" />
                  {file ? (
                    <div><p className="text-sm font-medium text-foreground">{file.name}</p><p className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB</p></div>
                  ) : (
                    <div><p className="text-sm text-muted-foreground">Clique para selecionar</p><p className="text-xs text-muted-foreground/60 mt-1">CSV ou XLSX</p></div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={handleFileSelect} className="hidden" />

                {file && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={() => handleUpload('manual')} disabled={parsing} variant="default" className="h-10">
                      {parsing && importMode === 'manual' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Importar Manual
                    </Button>
                    <Button onClick={() => handleUpload('ai')} disabled={parsing} variant="outline" className="h-10">
                      {parsing && importMode === 'ai' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Importar com IA
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Step 2: Manual Mapping */}
            {mappingStep && rawData && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{rawData.total_data_rows} linha(s) — Mapeie as colunas</p>
                  <button onClick={resetImport} className="text-xs text-muted-foreground hover:text-foreground">Cancelar</button>
                </div>

                {/* Column mapping selectors */}
                <div className="space-y-2">
                  {rawData.headers.map((header, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-muted/20 border border-border/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono font-bold truncate">{header || `Coluna ${idx}`}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{rawData.preview_rows?.[0]?.[idx] || '—'}</p>
                      </div>
                      <select
                        value={getFieldForCol(idx)}
                        onChange={(e) => setMappingField(e.target.value, idx)}
                        className="h-8 text-xs rounded-md border border-input bg-background px-2 w-40"
                      >
                        {FIELD_OPTIONS.map(o => (
                          <option key={o.value} value={o.value} disabled={o.value && colMapping[o.value] !== undefined && colMapping[o.value] !== idx}>
                            {o.label}{o.value && colMapping[o.value] !== undefined && colMapping[o.value] !== idx ? ' (já usado)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Preview */}
                {rawData.preview_rows?.length > 0 && (
                  <div className="max-h-32 overflow-auto rounded-lg border border-border text-[10px]">
                    <table className="w-full">
                      <thead className="bg-muted/50 sticky top-0"><tr>
                        {rawData.headers.map((h, i) => <th key={i} className="p-1.5 text-left font-medium">{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {rawData.preview_rows.slice(0, 3).map((r, ri) => (
                          <tr key={ri} className="border-t border-border/30">{r.map((c, ci) => <td key={ci} className="p-1.5 truncate max-w-[100px]">{c}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <Button onClick={handleApplyMapping} disabled={parsing || colMapping.name === undefined || colMapping.phone === undefined} className="w-full h-10">
                  {parsing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Processar Mapeamento
                </Button>
                {(colMapping.name === undefined || colMapping.phone === undefined) && (
                  <p className="text-xs text-yellow-500 text-center">Selecione ao menos Nome e Telefone</p>
                )}
              </div>
            )}

            {/* Step 3: Parsed preview */}
            {parsed && !importResult && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">{parsed.total_rows} cliente(s) encontrado(s)</p>
                  <button onClick={resetImport} className="text-xs text-muted-foreground hover:text-foreground">Cancelar</button>
                </div>

                {missingPlans.length > 0 && (
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 space-y-2">
                    <p className="text-xs font-medium text-yellow-500 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Pacotes não encontrados:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {missingPlans.map(p => (
                        <button key={p} onClick={() => { setPlanModal(p); setPlanPrice(''); }} className="flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-500/15 text-yellow-500 text-xs hover:bg-yellow-500/25 transition-colors"><Plus className="w-3 h-3" />{p}</button>
                      ))}
                    </div>
                    <p className="text-[10px] text-yellow-500/70">Clique para criar. Sem pacote = importa sem plano.</p>
                  </div>
                )}

                {duplicateCount > 0 && (
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-orange-500 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{duplicateCount} já cadastrado(s)</p>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-[10px] text-orange-500/70">{includeDuplicates ? 'Importar' : 'Ignorar'}</span>
                        <div className="relative">
                          <input type="checkbox" checked={includeDuplicates} onChange={e => setIncludeDuplicates(e.target.checked)} className="sr-only peer" />
                          <div className="w-8 h-4 bg-muted rounded-full peer peer-checked:bg-orange-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4"></div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                <div className="max-h-52 overflow-auto rounded-lg border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0"><tr>
                      <th className="p-2 text-left font-medium">Nome</th>
                      <th className="p-2 text-left font-medium">Telefone</th>
                      <th className="p-2 text-left font-medium">Pacote</th>
                      <th className="p-2 text-right font-medium">Valor</th>
                      <th className="p-2 text-right font-medium">Vencimento</th>
                    </tr></thead>
                    <tbody>
                      {parsed.customers.slice(0, 50).map((c, i) => (
                        <tr key={i} className={`border-t border-border/50 ${c.duplicate ? 'bg-orange-500/5' : ''}`}>
                          <td className="p-2 truncate max-w-[120px]">{c.name}{c.duplicate && <span className="block text-[10px] text-orange-400">⚠ {c.existing_name}</span>}</td>
                          <td className="p-2 font-mono">{c.phone}</td>
                          <td className="p-2">{c.plan_id ? <span className="text-green-500">{c.plan_name}</span> : c.plan_name ? <span className="text-yellow-500">{c.plan_name}</span> : <span className="text-muted-foreground">—</span>}</td>
                          <td className="p-2 text-right">R$ {(c.price || 0).toFixed(2)}</td>
                          <td className="p-2 text-right text-muted-foreground">{c.expiration ? new Date(c.expiration + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {importing && (<div className="space-y-2"><Progress value={importProgress} className="h-2" /><p className="text-xs text-center text-muted-foreground">{importProgress}%</p></div>)}

                {!importing && (
                  <Button onClick={handleImport} className="w-full h-10 bg-blue-600 hover:bg-blue-700">
                    <Upload className="w-4 h-4 mr-2" />Importar {includeDuplicates ? parsed.total_rows : parsed.total_rows - duplicateCount} Cliente(s)
                  </Button>
                )}
              </div>
            )}

            {/* Step 4: Result */}
            {importResult && (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-bold text-green-500">{importResult.imported} importado(s)</p>
                  {importResult.skipped > 0 && <p className="text-xs text-muted-foreground mt-1">{importResult.skipped} ignorado(s)</p>}
                </div>
                <Button onClick={resetImport} variant="outline" className="w-full">Nova Importação</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* EXPORT */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><Download className="w-4 h-4 text-green-500" />Exportar Clientes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Exporte com <strong>Nome, Telefone, Pacote, Mensalidade, Vencimento, Status e Aniversário</strong>.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => handleExport('csv')} disabled={exporting} className="flex flex-col items-center gap-3 p-6 rounded-xl bg-muted/30 border border-border hover:border-green-500/40 transition-colors group disabled:opacity-50">
                <FileSpreadsheet className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
                <div className="text-center"><p className="text-sm font-bold text-foreground">CSV</p><p className="text-[10px] text-muted-foreground">Compatível com tudo</p></div>
              </button>
              <button onClick={() => handleExport('xlsx')} disabled={exporting} className="flex flex-col items-center gap-3 p-6 rounded-xl bg-muted/30 border border-border hover:border-blue-500/40 transition-colors group disabled:opacity-50">
                <FileSpreadsheet className="w-8 h-8 text-blue-500 group-hover:scale-110 transition-transform" />
                <div className="text-center"><p className="text-sm font-bold text-foreground">Excel</p><p className="text-[10px] text-muted-foreground">Formatado (.xlsx)</p></div>
              </button>
            </div>
            {exporting && <div className="flex items-center justify-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /><span className="text-xs text-muted-foreground">Gerando...</span></div>}
          </CardContent>
        </Card>
      </div>

      {/* Create Plan Modal */}
      <Dialog open={!!planModal} onOpenChange={o => { if (!o) setPlanModal(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-yellow-500" />Criar Pacote</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label className="text-xs">Nome</Label><Input value={planModal || ''} disabled className="h-9 mt-1 bg-muted" /></div>
            <div><Label className="text-xs">Preço (R$)</Label><Input type="number" min="0" step="0.01" placeholder="0.00" value={planPrice} onChange={e => setPlanPrice(e.target.value)} className="h-9 mt-1" autoFocus /></div>
            <Button onClick={handleCreatePlan} disabled={creatingPlan} className="w-full">{creatingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar e Continuar'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
